import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, isSuperAdmin, type ZifekUser } from '@/hooks/useAuth';
import { useTenant } from '@/hooks/useTenant';
import { TYPE_COMPTE_VENDEUR } from '@/lib/marketplace';
import { fetchVendorRelation } from '@/lib/marketplaceCatalog';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import TenantColorBridge from '@/components/feature/TenantColorBridge';

export default function LoginClientPage() {
  const { user, login, loading } = useAuth();
  const { tenant } = useTenant();
  const navigate = useNavigate();

  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Redirection après connexion selon le type de compte. Un compte VENDEUR
  // (typecompte 9) qui possède un rattachement vendeur sur CETTE marketplace
  // est envoyé vers son tableau de bord vendeur ; sinon vers le dashboard.
  const redirectForUser = useCallback(async (target: ZifekUser) => {
    if (isSuperAdmin(target.typecompte)) {
      navigate('/superadmin', { replace: true });
      return;
    }
    if (target.typecompte === 6) {
      navigate('/mon-compte', { replace: true });
      return;
    }
    if (target.typecompte === 8) {
      navigate('/developpeur', { replace: true });
      return;
    }
    if (target.typecompte === TYPE_COMPTE_VENDEUR) {
      let vendorHere = false;
      if (tenant?.id) {
        try {
          const rel = await fetchVendorRelation(Number(tenant.id), target.id);
          vendorHere = !!rel;
        } catch {
          vendorHere = false;
        }
      }
      navigate(vendorHere ? '/vendor-dashboard' : '/dashboard', { replace: true });
      return;
    }
    navigate('/dashboard', { replace: true });
  }, [navigate, tenant?.id]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    redirectForUser(user).catch(() => {
      if (!cancelled) navigate('/dashboard', { replace: true });
    });
    return () => { cancelled = true; };
  }, [user, redirectForUser, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!identifier || !password) {
      setError('Veuillez remplir tous les champs.');
      return;
    }

    const result = await login(identifier, password);
    if (result.success && result.user) {
      // La redirection est gérée par l'effet ci-dessus (dépend du tenant).
      await redirectForUser(result.user);
    } else {
      setError(result.error || 'Erreur de connexion.');
    }
  };

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <TenantColorBridge />
      <Navbar forceScrolled={true} />

      <main className="flex-1 flex items-center justify-center px-4 pt-20 pb-10">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-14 h-14 rounded-2xl bg-accent-100 flex items-center justify-center mx-auto mb-4">
              <i className="ri-user-smile-line text-2xl text-accent-600"></i>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-2">
              Bienvenue sur Zifek
            </h1>
            <p className="text-foreground-500 text-sm">
              Connectez-vous à votre compte client
            </p>
          </div>

          <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 md:p-8">
            {error && (
              <div className="mb-5 p-3 rounded-md bg-red-50/80 border border-red-100 text-red-700 text-sm flex items-start gap-2">
                <i className="ri-error-warning-line flex-shrink-0 mt-0.5"></i>
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="identifier" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  Email ou nom d'utilisateur
                </label>
                <input
                  id="identifier"
                  type="text"
                  name="identifier"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  placeholder="vous@exemple.com ou nomutilisateur"
                  required
                  className="w-full h-11 px-4 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-accent-300 focus:ring-1 focus:ring-accent-300 transition-colors"
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  Mot de passe
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Votre mot de passe"
                    required
                    className="w-full h-11 px-4 pr-10 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-accent-300 focus:ring-1 focus:ring-accent-300 transition-colors"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-600 cursor-pointer"
                    aria-label={showPassword ? 'Cacher le mot de passe' : 'Afficher le mot de passe'}
                  >
                    <i className={`text-lg ${showPassword ? 'ri-eye-off-line' : 'ri-eye-line'}`}></i>
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full h-11 bg-foreground-950 text-background-50 rounded-full text-sm font-semibold whitespace-nowrap hover:bg-foreground-800 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Connexion...
                  </>
                ) : (
                  'Se connecter'
                )}
              </button>

              <div className="text-right">
                <Link to="/mot-de-passe-oublie" className="text-xs text-foreground-500 hover:text-accent-600 font-medium transition-colors">
                  Mot de passe oublié ?
                </Link>
              </div>
            </form>

            <div className="mt-6">
              <div className="relative mb-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-background-200/70"></div>
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="px-3 bg-background-50 text-foreground-400">Pas encore de compte ?</span>
                </div>
              </div>

              <Link
                to="/register-client"
                className="w-full h-11 border-2 border-background-200/70 text-foreground-600 rounded-full text-sm font-semibold whitespace-nowrap hover:bg-background-100 hover:border-background-300/70 transition-all cursor-pointer flex items-center justify-center gap-2"
              >
                <i className="ri-user-add-line text-sm"></i>
                Créer un compte client
              </Link>
            </div>

            <div className="mt-6 pt-5 border-t border-background-200/70 text-center">
              <p className="text-xs text-foreground-400 mb-2">Vous êtes un professionnel ?</p>
              <Link to="/login" className="text-sm text-foreground-600 font-medium hover:text-foreground-950 transition-colors">
                Espace professionnel →
              </Link>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}